/**
 * Memory notes as facts: one fact per line, with the provenance auto-capture appends
 * (" [auto-saved by Chief, 2026-11-05, message …]") and a source (" [source: …]") parsed off, so
 * people only ever see the fact itself.
 */

const SOURCE_SUFFIX = / \[source: ([^\]]*)\]$/;
const AUTO_SUFFIX = / \[auto-saved ([^\]]*)\]$/;
const ISO_DATE = /\b(\d{4}-\d{2}-\d{2})\b/;

/** The fact lines of a note: list items and plain lines, not headings, fences or blank lines. */
export function factLines(content: string): string[] {
  return content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#") && !line.startsWith("```"));
}

/** A note line as a fact: its text without the bullet and provenance, and what that said. */
export function parseFactLine(line: string): {
  text: string;
  date: string | null;
  savedBy: string | null;
  source: string | null;
} {
  let text = line.trim().replace(/^[-*]\s+/, "");
  let source: string | null = null;
  let savedBy: string | null = null;
  let date: string | null = null;
  const sourced = SOURCE_SUFFIX.exec(text);
  if (sourced) {
    source = sourced[1]!.trim();
    text = text.slice(0, sourced.index);
  }
  const auto = AUTO_SUFFIX.exec(text);
  if (auto) {
    const parts = auto[1]!.split(",").map((part) => part.trim());
    savedBy = parts.find((part) => part.startsWith("by "))?.slice(3) ?? null;
    date = parts.find((part) => ISO_DATE.test(part)) ?? null;
    text = text.slice(0, auto.index);
  }
  const asOf = /^As of (\d{4}-\d{2}-\d{2}):\s*/.exec(text);
  if (asOf) date = asOf[1]!;
  return { text: text.trim(), date, savedBy, source };
}

/**
 * A note line as people read it in a summary: the fact and the document it came from, never
 * the auto-save provenance (who saved it, when, from which message).
 */
export function factDisplayText(line: string): string {
  const { text, source } = parseFactLine(line);
  return source ? `${text} [source: ${source}]` : text;
}
